import React, { useEffect, useState } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";

const AddAddress = ({ addressToEdit, closeModal }) => {
  const [userId, setUserId] = useState("");
  const [inValidPincode ,setInValidPincode] = useState(false);
  const token = localStorage.getItem("STITCHD_ID");
  const [addressDetails, setAddressDetails] = useState({
    name: "",
    phone: "",
    address: "",
    landmark: "",
    postalCode: "",
    city: "",
  });

  // State to manage form validation errors
  const [formErrors, setFormErrors] = useState({
    name: false,
    phone: false,
    address: false,
    postalCode: false,
    city: false,
    landmark: false,
  });

  useEffect(() => {
    if (token) {
      const decoded = jwtDecode(token);
      const id = decoded._id;
      setUserId(id);
    }
  }, [token]);

  useEffect(() => {
    if (addressToEdit) {
      setAddressDetails({
        name: addressToEdit.name,
        phone: addressToEdit.phone,
        address: addressToEdit.address,
        landmark: addressToEdit.landmark,
        postalCode: addressToEdit.postalCode,
        city: addressToEdit.city,
      });
    }
  }, [addressToEdit]);

  const handleChange = async (e) => {
    const { name, value } = e.target;
    setAddressDetails({ ...addressDetails, [name]: value });

    if (name === "postalCode" && value.length === 6) {
      try {
        const response = await axios.get(
          `https://api.postalpincode.in/pincode/${value}`
        );
        const data = response.data[0];

        if (data.Status === "Success" && data.PostOffice) {
          setInValidPincode(false)
          setAddressDetails((prevDetails) => ({
            ...prevDetails,
            city: data.PostOffice[0].District,
          }));
        } else {
          setInValidPincode(true)
          setAddressDetails((prevDetails) => ({
            ...prevDetails,
            city: "",
          }));
        }
      } catch (error) {
        
        setAddressDetails((prevDetails) => ({
          ...prevDetails,
          city: "",
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form before submitting
    const { name, phone, address, postalCode, city, landmark } = addressDetails;
    const errors = {
      name: name === "",
      phone: phone.length !== 10 && addressToEdit?.phone !== phone,
      address: address === "",
      postalCode: postalCode.length !== 6 && addressToEdit?.postalCode !== postalCode,
      city: city === "",
      landmark: landmark === "",
    };

    setFormErrors(errors);

    // Check if there are any errors
    if (Object.values(errors).some((error) => error)) {
      return; // If there are errors, prevent form submission
    }

    try {
      let response;
      if (addressToEdit) {
        // Update existing address
        const payload = { ...addressDetails, user: userId };
        response = await axios.patch(
          `${process.env.REACT_APP_URL}/address/update/${addressToEdit._id}`,
          payload
        );
      } else {
        // Add new address
        const payload = { ...addressDetails, user: userId };
        response = await axios.post(
          `${process.env.REACT_APP_URL}/address/add`,
          payload
        );
      }
      if (response.data.success) {
        closeModal();
      }
    } catch (error) {
      console.error("Error saving address:", error);
    }
  };

  return (
    <div className="md:p-10 p-2 max-w-4xl mx-auto bg-white border rounded-lg shadow-md font-twentieth-century">
      <h2 className="text-2xl font-bold mb-6 text-center">
        {addressToEdit ? "Edit Address" : "Add New Address"}
      </h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex flex-wrap -mx-2">
          <div className="w-full md:w-1/2 px-2 mb-4 md:mb-0">
            <label
              className="block text-gray-600 font-medium mb-2"
              htmlFor="name"
            >
              Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={addressDetails.name}
              onChange={handleChange}
              className={`w-full border p-2 rounded ${
                formErrors.name ? "border-red-500" : ""
              }`}
              required
            />
            {formErrors.name && (
              <p className="text-red-500 text-xs mt-1">Name is required</p>
            )}
          </div>
          <div className="w-full md:w-1/2 px-2">
            <label
              className="block text-gray-600 font-medium mb-2"
              htmlFor="phone"
            >
              Phone Number
            </label>
            <input
              type="text"
              id="phone"
              name="phone"
              value={addressDetails.phone}
              onChange={handleChange}
              className={`w-full border p-2 rounded ${
                formErrors.phone ? "border-red-500" : ""
              }`}
              required
            />
            {formErrors.phone && (
              <p className="text-red-500 text-xs mt-1">
                Phone number must be 10 digits
              </p>
            )}
          </div>
        </div>
        <div>
          <label
            className="block text-gray-600 font-medium mb-2"
            htmlFor="address"
          >
            Address
          </label>
          <input
            type="text"
            id="address"
            name="address"
            value={addressDetails.address}
            onChange={handleChange}
            className={`w-full border p-2 rounded ${
              formErrors.address ? "border-red-500" : ""
            }`}
            required
          />
          {formErrors.address && (
            <p className="text-red-500 text-xs mt-1">Address is required</p>
          )}
        </div>
        <div>
          <label
            className="block text-gray-600 font-medium mb-2"
            htmlFor="landmark"
          >
            Landmark
          </label>
          <input
            type="text"
            id="landmark"
            name="landmark"
            value={addressDetails.landmark}
            onChange={handleChange}
            className={`w-full border p-2 rounded ${
              formErrors.landmark ? "border-red-500" : ""
            }`}
            required
          />
          {formErrors.landmark && (
            <p className="text-red-500 text-xs mt-1">Landmark is required</p>
          )}
        </div>
        <div className="flex flex-wrap -mx-2">
          <div className="w-full md:w-1/2 px-2 mb-4 md:mb-0">
            <label
              className="block text-gray-600 font-medium mb-2"
              htmlFor="postalCode"
            >
              Postal Code
            </label>
            <input
              type="text"
              id="postalCode"
              name="postalCode"
              value={addressDetails.postalCode}
              onChange={handleChange}
              className={`w-full border p-2 rounded ${
                formErrors.postalCode ? "border-red-500" : ""
              }`}
              required
            />
            {formErrors.postalCode || inValidPincode ? (
              <p className="text-red-500 text-xs mt-1">
                Please enter valid pincode
              </p>
            ): ''}
          </div>
          <div className="w-full md:w-1/2 px-2">
            <label
              className="block text-gray-600 font-medium mb-2"
              htmlFor="city"
            >
              City
            </label>
            <input
              type="text"
              id="city"
              name="city"
              value={addressDetails.city}
              onChange={handleChange}
              className={`w-full border p-2 rounded ${
                formErrors.city ? "border-red-500" : ""
              }`}
              required
              readOnly
            />
            {formErrors.city && (
              <p className="text-red-500 text-xs mt-1">City is required</p>
            )}
          </div>
        </div>
        <div className="text-center">
          <button
            type="submit"
            className="bg-red-500 text-white px-6 py-2 rounded shadow hover:bg-red-600 transition duration-200"
          >
            {addressToEdit ? "Save Changes" : "Add Address"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddAddress;
